import { CXScenarioConfig } from '@/config/cx-scenarios';
import { getTranslations } from '@/config/cx-translations';
import api from '@/utils/ky';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Define types for assessment data
interface FeedbackItem {
  why: string;
  suggestion: string;
}

interface AspectScore {
  score: number;
  maxScore: number;
  feedback: FeedbackItem;
}

interface PolicyApplicationItem {
  type: string;
  message: string;
  correction?: string;
}

interface AssessmentData {
  serviceDelivery: {
    score: number;
    description: string;
    greetingIdentityVerification: AspectScore;
    problemUnderstanding: AspectScore;
    empathyTone: AspectScore;
    resolutionRecovery: AspectScore;
  };
  policyApplication: {
    score: number;
    description: string;
    strengths: string[];
    toImprove: PolicyApplicationItem[];
  };
  practiceInfo: any;
  summary: string;
  suggestedNextSteps: string[];
}

interface CXAssessmentBaseProps {
  scenario: CXScenarioConfig;
  apiBasePath: string; // e.g., 'api/cx/grab' or 'api/cx/miso'
  roleplayPath: string; // e.g., '/cx/grab/roleplay'
}

// Function to fetch and transform assessment data
const fetchAssessment = async (apiBasePath: string, sessionId: string | undefined) => {
  if (!sessionId) throw new Error('Session ID is required');

  const data = await api
    .get(`${apiBasePath}/sessions/${sessionId}/assessment`)
    .json<AssessmentData>();

  if (!data) {
    throw new Error('No assessment data available');
  }

  return data;
};

export function CXAssessmentBase({ scenario, apiBasePath, roleplayPath }: CXAssessmentBaseProps) {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const [loadingStates, setLoadingStates] = useState({
    overview: false,
    serviceDelivery: false,
    policyApplication: false,
  });

  // Get translations based on scenario language
  const t = getTranslations(scenario.language);

  // Use React Query to fetch assessment data
  const {
    data: assessmentData,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['assessment', apiBasePath, sessionId],
    queryFn: () => fetchAssessment(apiBasePath, sessionId),
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3, // Retry failed requests 3 times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });

  console.log('assessmentData', assessmentData);

  const getRingColor = (percentage: number, isInner?: boolean) => {
    if (isInner) {
      if (percentage >= 80) return '#A5E0CE'; // green
      if (percentage >= 60) return '#ABCEF8'; // blue
      if (percentage >= 40) return '#FFBE85'; // orange
      return '#FF9F99'; // red
    } else {
      if (percentage >= 80) return '#38A383'; // green
      if (percentage >= 60) return '#1C7AEB'; // blue
      if (percentage >= 40) return '#FF7E0D'; // orange
      return '#E60D00'; // red
    }
  };

  const ScoreCircle = ({
    score,
    maxScore,
    size = 'md',
    label,
    ringColor,
    isInner,
    showDenominator,
  }: {
    score: number;
    maxScore: number;
    size?: 'sm' | 'md' | 'lg' | 'l';
    label?: string;
    ringColor?: string;
    isInner?: boolean;
    showDenominator?: boolean;
  }) => {
    const percentage = (score / maxScore) * 100;
    const radius =
      size === 'l' ? 40 : size === 'lg' ? 45 : size === 'md' ? 35 : 25;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    const getColor = (percentage: number) => {
      if (ringColor) return ringColor;

      return getRingColor(percentage, isInner);
    };

    return (
      <div className="flex flex-col items-center">
        <div
          className={`relative ${
            size === 'l'
              ? 'w-22 h-20'
              : size === 'lg'
              ? 'w-24 h-24'
              : size === 'md'
              ? 'w-20 h-20'
              : 'w-16 h-16'
          }`}
        >
          <svg
            className="transform -rotate-90 w-full h-full"
            viewBox="0 0 100 100"
          >
            <circle
              cx="50"
              cy="50"
              r={radius}
              stroke="#e5e7eb"
              strokeWidth="8"
              fill="none"
            />
            <circle
              cx="50"
              cy="50"
              r={radius}
              stroke={getColor(percentage)}
              strokeWidth="8"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-300"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex items-center">
              <span
                className={`font-semibold ${
                  size === 'lg'
                    ? 'text-lg'
                    : size === 'md'
                    ? 'text-base'
                    : 'text-sm'
                }`}
              >
                {score}
              </span>
              {showDenominator && (
                <span className="text-gray-400 text-xs ml-0.5">
                  /{maxScore}
                </span>
              )}
            </div>
          </div>
        </div>
        {label && (
          <span className="text-xs text-gray-600 mt-2 text-center max-w-20">
            {label}
          </span>
        )}
      </div>
    );
  };

  const simulateLoading = (section: string) => {
    setLoadingStates((prev) => ({ ...prev, [section]: true }));
    setTimeout(() => {
      setLoadingStates((prev) => ({ ...prev, [section]: false }));
    }, 1500);
  };

  // Add custom labels not in translation system
  const labels = {
    ...t,
    greetingIdentity: scenario.serviceDelivery.greetingIdentity.title,
    problemUnderstanding: scenario.serviceDelivery.problemUnderstanding.title,
    empathyTone: scenario.serviceDelivery.empathyTone.title,
    resolutionRecovery: scenario.serviceDelivery.resolutionRecovery.title,
    noStrengths: scenario.language === 'ko'
      ? '정책 적용에서 확인된 강점이 없습니다. 회사 정책에 대한 이해도를 높이는 데 집중하세요.'
      : `No strengths identified in policy application. Focus on improving your understanding of ${scenario.companyName} policies.`,
    perfectPolicy: scenario.language === 'ko'
      ? '완벽한 정책 적용! 이 상호작용에서 모든 회사 정책을 올바르게 적용했습니다.'
      : `Perfect policy application! You've correctly applied all ${scenario.companyName} policies in this interaction.`
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">{t.loadingAssessmentData}</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (queryError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-sm max-w-md text-center">
          <XCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t.failedToLoadAssessment}
          </h2>
          <p className="text-gray-600 mb-6">
            {queryError instanceof Error
              ? queryError.message
              : t.failedToLoadAssessmentData}
          </p>
          <button
            onClick={() => navigate(roleplayPath)}
            className="px-6 py-3 bg-orange-500 text-white rounded-full hover:bg-orange-600 font-medium text-sm"
          >
            {t.returnToPractice}
          </button>
        </div>
      </div>
    );
  }

  // No assessment data available
  if (!assessmentData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-sm max-w-md text-center">
          <ExclamationTriangleIcon className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t.noAssessmentAvailable}
          </h2>
          <p className="text-gray-600 mb-6">
            {t.noAssessmentDataAvailable}
          </p>
          <button
            onClick={() => navigate(roleplayPath)}
            className="px-6 py-3 bg-orange-500 text-white rounded-full hover:bg-orange-600 font-medium text-sm"
          >
            {t.returnToPractice}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center">
              <img
                src="/logos/Hupo_Icon_Orange.svg"
                alt="Logo"
                className="size-7"
              />
            </div>
            <h1 className="text-lg font-semibold text-gray-900">
              {labels.roleplayAssessment}
            </h1>
          </div>
        </div>
      </header>

      <div className="flex h-[calc(100vh-80px)]">
        {/* Left Panel - Overview */}
        <div className="w-80 overflow-y-auto p-4">
          <div className="p-6 bg-white rounded-xl shadow-sm">
            {/* Overall Score Section */}
            <div className="mb-8">
              <div className="flex items-center space-x-4 mb-6">
                <div className="relative">
                  {/* Outer ring - Service Delivery */}
                  <ScoreCircle
                    score={assessmentData.serviceDelivery.score}
                    maxScore={100}
                    size="lg"
                  />
                  {/* Inner ring - Policy Application */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="scale-75">
                      <ScoreCircle
                        score={assessmentData.policyApplication.score}
                        maxScore={100}
                        size="lg"
                        isInner
                      />
                    </div>
                  </div>
                  {/* Overall score in the center */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-white rounded-full p-2 text-xl font-bold">
                      {(
                        Math.round(
                          ((assessmentData.serviceDelivery.score +
                            assessmentData.policyApplication.score) /
                            2) *
                            2,
                        ) / 2
                      )
                        .toFixed(1)
                        .replace(/\.0$/, '')}
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-600 mb-1">
                    {labels.overallScore}
                  </h3>
                  <p className="text-lg font-semibold text-gray-900">
                    {(() => {
                      const score =
                        (assessmentData.serviceDelivery.score +
                          assessmentData.policyApplication.score) /
                        2;
                      if (score >= 90) return labels.excellent;
                      if (score >= 70) return labels.good;
                      if (score >= 50) return labels.fair;
                      return labels.poor;
                    })()}
                  </p>
                </div>
              </div>

              {/* Score Breakdown */}
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-1 h-6 bg-[${getRingColor(
                      assessmentData.serviceDelivery.score,
                    )}] rounded-full`}
                  ></div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">
                        {labels.serviceDelivery}
                      </span>
                      <div className="text-sm text-gray-600">
                        <span className="font-medium text-gray-900">
                          {assessmentData.serviceDelivery.score}
                        </span>
                        <span className="text-gray-400"> / {100}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-1 h-6 bg-[${getRingColor(
                      assessmentData.policyApplication.score,
                      true,
                    )}] rounded-full`}
                  ></div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">
                        {labels.policyApplication}
                      </span>
                      <div className="text-sm text-gray-600">
                        <span className="font-medium text-gray-900">
                          {assessmentData.policyApplication.score}
                        </span>
                        <span className="text-gray-400"> / {100}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Practice Info */}
            <div className="mb-8">
              <h4 className="text-sm font-semibold text-gray-900 mb-4">
                {labels.practiceInfo}
              </h4>
              <div className="space-y-4">
                <div>
                  <span className="text-sm text-gray-600 block mb-1">{labels.type}</span>
                  <span className="text-sm font-medium text-gray-900 block">
                    {scenario.title}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-600 block mb-1">{labels.guest}</span>
                  <span className="text-sm font-medium text-gray-900 block">
                    {scenario.persona.name}, {scenario.persona.role}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-600 block mb-1">{labels.completedAt}</span>
                  <span className="text-sm font-medium text-gray-900 block">
                    {moment(new Date()).format('DD MMM YYYY')}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={() => navigate(roleplayPath)}
                className="w-full px-6 py-3 bg-[#FF4B0A] text-white rounded-full hover:bg-orange-600 font-medium text-sm"
              >
                {labels.practiceAgain}
              </button>
            </div>
          </div>
        </div>

        {/* Main Content - Single Scrollable Page */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-6">
            {/* Overview Section */}
            <section
              id="overview"
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-gray-900">
                  {labels.overview}
                </h2>
              </div>

              <div className="space-y-6">
                {/* Summary */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {labels.summary}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {assessmentData.summary}
                  </p>
                </div>

                {/* Suggested Next Steps */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {labels.suggestedNextSteps}
                  </h3>
                  <ul className="space-y-2">
                    {assessmentData.suggestedNextSteps.map((step, index) => (
                      <li
                        key={index}
                        className="flex items-start text-sm text-gray-600"
                      >
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        {step}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* Service Delivery Section */}
            <section
              id="service-delivery"
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl font-semibold text-gray-900">
                  {labels.serviceDelivery}
                </h2>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">{labels.overallScore}</span>
                    <ScoreCircle
                      score={assessmentData.serviceDelivery.score}
                      maxScore={100}
                      showDenominator={true}
                      size="l"
                    />
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                {assessmentData.serviceDelivery.description || (scenario.language === 'ko'
                  ? '커뮤니케이션 및 고객 경험 관점에서 상호작용이 얼마나 효과적으로 처리되었는지 평가합니다.'
                  : 'Assesses how effectively the interaction was handled from a communication and customer experience standpoint.')}
              </p>

              {/* Service Delivery Aspects */}
              <div className="space-y-6">
                {/* Greeting & Identity Verification */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-medium text-gray-900">
                      {labels.greetingIdentity}
                    </h4>
                    <div className="flex items-center space-x-1">
                      <span className="text-lg font-semibold">
                        {assessmentData.serviceDelivery.greetingIdentityVerification.score}
                      </span>
                      <span className="text-gray-400">
                        / {assessmentData.serviceDelivery.greetingIdentityVerification.maxScore}
                      </span>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 my-4"></div>

                  <div className="space-y-4">
                    <div>
                      <div className="flex items-start mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          {labels.why}:
                        </span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-gray-500 mr-2 mt-1">•</span>
                        <p className="text-sm text-gray-600">
                          {assessmentData.serviceDelivery.greetingIdentityVerification.feedback.why}
                        </p>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-start mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          {labels.suggestion}:
                        </span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-gray-500 mr-2 mt-1">•</span>
                        <p className="text-sm text-gray-600">
                          {assessmentData.serviceDelivery.greetingIdentityVerification.feedback.suggestion}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Problem Understanding */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-medium text-gray-900">
                      {labels.problemUnderstanding}
                    </h4>
                    <div className="flex items-center space-x-1">
                      <span className="text-lg font-semibold">
                        {assessmentData.serviceDelivery.problemUnderstanding.score}
                      </span>
                      <span className="text-gray-400">
                        / {assessmentData.serviceDelivery.problemUnderstanding.maxScore}
                      </span>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 my-4"></div>

                  <div className="space-y-4">
                    <div>
                      <div className="flex items-start mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          {labels.why}:
                        </span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-gray-500 mr-2 mt-1">•</span>
                        <p className="text-sm text-gray-600">
                          {assessmentData.serviceDelivery.problemUnderstanding.feedback.why}
                        </p>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-start mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          {labels.suggestion}:
                        </span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-gray-500 mr-2 mt-1">•</span>
                        <p className="text-sm text-gray-600">
                          {assessmentData.serviceDelivery.problemUnderstanding.feedback.suggestion}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Empathy & Tone */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-medium text-gray-900">
                      {labels.empathyTone}
                    </h4>
                    <div className="flex items-center space-x-1">
                      <span className="text-lg font-semibold">
                        {assessmentData.serviceDelivery.empathyTone.score}
                      </span>
                      <span className="text-gray-400">
                        / {assessmentData.serviceDelivery.empathyTone.maxScore}
                      </span>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 my-4"></div>

                  <div className="space-y-4">
                    <div>
                      <div className="flex items-start mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          {labels.why}:
                        </span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-gray-500 mr-2 mt-1">•</span>
                        <p className="text-sm text-gray-600">
                          {assessmentData.serviceDelivery.empathyTone.feedback.why}
                        </p>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-start mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          {labels.suggestion}:
                        </span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-gray-500 mr-2 mt-1">•</span>
                        <p className="text-sm text-gray-600">
                          {assessmentData.serviceDelivery.empathyTone.feedback.suggestion}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Resolution & Recovery */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-medium text-gray-900">
                      {labels.resolutionRecovery}
                    </h4>
                    <div className="flex items-center space-x-1">
                      <span className="text-lg font-semibold">
                        {assessmentData.serviceDelivery.resolutionRecovery.score}
                      </span>
                      <span className="text-gray-400">
                        / {assessmentData.serviceDelivery.resolutionRecovery.maxScore}
                      </span>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 my-4"></div>

                  <div className="space-y-4">
                    <div>
                      <div className="flex items-start mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          {labels.why}:
                        </span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-gray-500 mr-2 mt-1">•</span>
                        <p className="text-sm text-gray-600">
                          {assessmentData.serviceDelivery.resolutionRecovery.feedback.why}
                        </p>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-start mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          {labels.suggestion}:
                        </span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-gray-500 mr-2 mt-1">•</span>
                        <p className="text-sm text-gray-600">
                          {assessmentData.serviceDelivery.resolutionRecovery.feedback.suggestion}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Policy Application Section */}
            <section
              id="policy-application"
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl font-semibold text-gray-900">
                  {labels.policyApplication}
                </h2>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">{labels.overallScore}</span>
                    <ScoreCircle
                      score={assessmentData.policyApplication.score}
                      maxScore={100}
                      size="l"
                      showDenominator={true}
                    />
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                {assessmentData.policyApplication.description || (scenario.language === 'ko'
                  ? '회사 정책이 얼마나 정확하게 적용되고 내부 표준과 프로토콜이 얼마나 잘 준수되는지 평가합니다.'
                  : 'Assesses how accurately company policies are applied and how well internal standards and protocols are followed.')}
              </p>

              {/* Policy Application Score */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-gray-900">
                    {labels.policyApplication}
                  </h4>
                  <div className="flex items-center space-x-2">
                    <span className="text-lg font-semibold">
                      {assessmentData.policyApplication.score}
                    </span>
                    <span className="text-gray-400"> / {100}</span>
                  </div>
                </div>
                <div className="border-t border-gray-200 my-4"></div>

                {/* Strengths */}
                <div className="mb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-3">
                    {labels.strengths}
                  </h4>
                  <div className="space-y-3">
                    {assessmentData.policyApplication.strengths.length > 0 ? (
                      assessmentData.policyApplication.strengths.map(
                        (strength, index) => (
                          <div
                            key={index}
                            className="flex items-start space-x-3"
                          >
                            <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-gray-600">{strength}</p>
                          </div>
                        ),
                      )
                    ) : (
                      <div className="flex items-start space-x-3">
                        <p className="text-sm text-gray-600 italic">
                          {labels.noStrengths}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* To Improve */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-3">
                    {labels.toImprove}
                  </h4>
                  <div className="space-y-3">
                    {assessmentData.policyApplication.toImprove.length > 0 ? (
                      assessmentData.policyApplication.toImprove.map(
                        (item, index) => (
                          <div
                            key={index}
                            className="flex items-start space-x-3"
                          >
                            {index === 0 ? (
                              <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                            ) : (
                              <XCircleIcon className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                            )}
                            <div>
                              <p className="text-sm text-gray-600">
                                {item.message}
                              </p>
                              {item.correction ? (
                                <div className="mt-2 p-3 bg-red-50 border-l-4 border-red-400 rounded">
                                  <p className="text-sm text-red-700">
                                    <strong>{labels.correction}:</strong>{' '}
                                    {item.correction}
                                  </p>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        ),
                      )
                    ) : (
                      <div className="flex items-start space-x-3">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-gray-600 italic">
                          {labels.perfectPolicy}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Right Panel - Navigation */}
        <div className="w-64 overflow-y-auto p-4">
          <div className="p-6 bg-white rounded-xl shadow-sm">
            <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">
              {labels.jumpToSection}
            </h4>
            <nav className="space-y-2">
              <button
                onClick={() => {
                  document
                    .getElementById('overview')
                    ?.scrollIntoView({ behavior: 'smooth' });
                  simulateLoading('overview');
                }}
                className="block w-full text-left px-3 py-2 text-sm rounded-lg text-gray-600 hover:bg-gray-50"
              >
                {labels.overview}
              </button>
              <button
                onClick={() => {
                  document
                    .getElementById('service-delivery')
                    ?.scrollIntoView({ behavior: 'smooth' });
                  simulateLoading('serviceDelivery');
                }}
                className="block w-full text-left px-3 py-2 text-sm rounded-lg text-gray-600 hover:bg-gray-50"
              >
                {labels.serviceDelivery}
              </button>
              <button
                onClick={() => {
                  document
                    .getElementById('policy-application')
                    ?.scrollIntoView({ behavior: 'smooth' });
                  simulateLoading('policyApplication');
                }}
                className="block w-full text-left px-3 py-2 text-sm rounded-lg text-gray-600 hover:bg-gray-50"
              >
                {labels.policyApplication}
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}